import { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const TRELLO_BOARD_URL = 'https://trello.com/b/Xvt6OijS';
const TRELLO_EMBED_SCRIPT_ID = 'trello-embed-script';
const TRELLO_EMBED_SCRIPT_SRC = 'https://p.trellocdn.com/embed.min.js';

export default function TrelloSuiviCommercialPage() {
  const embedRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Suivi commercial IA | Pulsia';

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobotsContent = robotsMeta?.getAttribute('content') ?? null;

    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }

    robotsMeta.setAttribute('content', 'noindex,nofollow,noarchive');

    return () => {
      document.title = previousTitle;

      if (robotsMeta) {
        if (previousRobotsContent === null) {
          robotsMeta.remove();
        } else {
          robotsMeta.setAttribute('content', previousRobotsContent);
        }
      }
    };
  }, []);

  useEffect(() => {
    const loadTrelloEmbed = () => {
      if (window.TrelloBoards && embedRef.current) {
        window.TrelloBoards.load(embedRef.current, { allAnchors: false });
      }
    };

    const existingScript = document.getElementById(TRELLO_EMBED_SCRIPT_ID);
    if (existingScript) {
      loadTrelloEmbed();
      return undefined;
    }

    const script = document.createElement('script');
    script.id = TRELLO_EMBED_SCRIPT_ID;
    script.src = TRELLO_EMBED_SCRIPT_SRC;
    script.async = true;
    script.onload = loadTrelloEmbed;
    document.body.appendChild(script);

    return undefined;
  }, []);

  return (
    <Layout>
      <section className="relative overflow-hidden pt-28">
        <div className="absolute inset-0 bg-gradient-subtle" />
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-dots-pattern opacity-25" />

        <div className="section-container relative z-10 py-16 sm:py-20">
          <div className="mx-auto mb-12 max-w-4xl text-center">
            <span className="section-label mb-6 inline-flex">
              Suivi commercial client
            </span>
            <h1 className="font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl">
              Visualisez les prospects collectes par votre{' '}
              <span className="gradient-text">agent IA</span>
            </h1>
            <p className="mx-auto mt-7 max-w-3xl text-xl leading-relaxed text-muted-foreground sm:text-2xl">
              Cette page vous permet de consulter le tableau Trello de suivi
              commercial ou apparaissent les cartes creees automatiquement
              lorsque votre agent Chatbottia recupere des informations dans le
              chat.
            </p>
          </div>

          <div className="gradient-border rounded-2xl bg-card/60 p-5 shadow-lg backdrop-blur-md sm:p-8">
            <div className="grid gap-8 lg:grid-cols-[1fr_360px] lg:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                  Tableau Trello de suivi des prospects
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  Trello n&apos;autorise pas l&apos;affichage complet d&apos;un
                  tableau interactif dans une iframe externe. Utilisez le bouton
                  ci-dessous pour ouvrir le tableau dans Trello et consulter les
                  cartes prospect creees par l&apos;agent IA.
                </p>
                <a
                  href={TRELLO_BOARD_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-7 inline-flex"
                >
                  <Button variant="hero" size="lg" className="btn-glow gap-2">
                    Ouvrir le tableau Trello
                    <ExternalLink className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </a>
              </div>

              <div
                ref={embedRef}
                className="flex min-h-[180px] items-center justify-center rounded-xl border border-border/50 bg-background/70 p-4"
              >
                <blockquote className="trello-board-compact">
                  <a href={TRELLO_BOARD_URL}>Tableau Trello Chatbottia</a>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
